/**
 * FieldList Component
 *
 * Smart component for managing a list of fields with survey-type-specific behavior.
 * Handles field operations and provides integration points for custom logic (like SatisfactionSurvey metadata).
 */

import { Button } from '@HoudiniUI/Button';
import { bs } from '@Styles/styles';
import { FormFieldType, IFormField } from '@ViewModels';
import { css } from 'aphrodite';
import * as React from 'react';
import { v4 as uuid } from 'uuid';
import { usePreviewMode } from '../../../contexts/PreviewModeContext';
import { FormFieldOptionTitles } from '../../FormFieldWithOptions';
import { styleSheet } from '../styles';
import {
	ConditionalQuestionControlsProps,
	ConditionalVisibilityProps,
	CustomFieldManagerProps,
	FieldListProps,
	TagManagerProps,
} from '../types';
import { QuestionField } from './QuestionField';

/**
 * FieldList component for managing a collection of form fields
 */
export const FieldList: React.FC<FieldListProps> = React.memo(
	({
		fields,
		isEdit,
		isClone = false,
		config,
		formState,
		tagGenerationSettings,
		customFieldMap,
		availableCustomFields = [],
		onFieldAdd,
		onFieldUpdate,
		onFieldRemove,
		onLabelChange,
		onToggleOptional,
		onFieldTypeChange,
		onTagPrefixChange,
		onTagPrefixDelete,
		onCustomFieldChange,
		onCustomFieldDelete,
		onConditionalToggle,
		onRatingThresholdChange,
		getFieldMetadata,
		isFieldDirty,
		onValidationTrigger,
		templateMode,
		conditionalVisibilityMap,
		onConditionalVisibilityToggle,
		onConditionalVisibilityQuestionSelect,
		onConditionalVisibilityAnswerSelect,
		onConditionalVisibilityAnswerAdd,
		onConditionalVisibilityAnswerRemove,
	}) => {
		// Get preview mode from context
		const { isPreviewOnly } = usePreviewMode();
		// Check if we can add more questions (respects question limits)
		const canAddQuestions = React.useMemo(() => {
			if (!config.fieldManagement.questionLimit) return true;
			return fields.length < config.fieldManagement.questionLimit;
		}, [fields.length, config.fieldManagement.questionLimit]);

		// Create new field with survey-type-specific defaults
		const createNewField = React.useCallback((): IFormField<string> => {
			const fieldId = uuid();
			return {
				fieldType: FormFieldType.String,
				id: fieldId,
				label: '',
				isHidden: false,
				isOptional: config.fieldManagement.defaultOptional,
			};
		}, [config.fieldManagement.defaultOptional]);

		// Handle adding a new field
		const handleAddField = React.useCallback(() => {
			if (!canAddQuestions) return;

			createNewField();
			onFieldAdd();
		}, [canAddQuestions, createNewField, onFieldAdd]);

		// Memoize form state errors to avoid recalculating on every render
		const formErrors = React.useMemo(() => {
			const customFormErrors = formState.errors?.customForm;
			// Type guard to check if customFormErrors has fields property
			if (!customFormErrors || typeof customFormErrors !== 'object' || !('fields' in customFormErrors)) {
				return [];
			}
			const fieldsArray = (customFormErrors as { fields?: unknown }).fields;
			return Array.isArray(fieldsArray) ? fieldsArray : [];
		}, [formState.errors?.customForm]);

		// Memoize tag generation settings to avoid recreating objects
		const memoizedTagGenerationSettings = React.useMemo(() => tagGenerationSettings, [tagGenerationSettings]);

		// Memoize conditional questions config
		const conditionalConfig = React.useMemo(() => config.conditionalQuestions, [config.conditionalQuestions]);

		// Memoize conditional visibility config ("Only show when")
		const conditionalVisibilityConfig = React.useMemo(
			() => config.conditionalVisibility,
			[config.conditionalVisibility]
		);

		// Memoize tag generation config to avoid object recreation
		const tagGenerationConfig = React.useMemo(
			() => ({
				enabled: config.tagGeneration.enabled,
				supportsTagPrefixes: config.tagGeneration.supportsTagPrefixes,
			}),
			[config.tagGeneration.enabled, config.tagGeneration.supportsTagPrefixes]
		);

		// Memoize custom field management config
		const customFieldConfig = React.useMemo(
			() => ({
				enabled: config.customFieldManagement.enabled,
				supportedFieldTypes: config.customFieldManagement.supportedFieldTypes,
			}),
			[config.customFieldManagement.enabled, config.customFieldManagement.supportedFieldTypes]
		);

		// Memoize custom field settings to avoid recreating objects
		const memoizedCustomFieldMap = React.useMemo(() => customFieldMap, [customFieldMap]);

		// Calculate used custom field IDs for disabled state
		const usedCustomFieldIds = React.useMemo(() => {
			if (!memoizedCustomFieldMap) return [];
			return memoizedCustomFieldMap.map((mapping: { contactFieldId: string }) => mapping.contactFieldId);
		}, [memoizedCustomFieldMap]);

		// Render individual field with proper configuration - optimized with minimal dependencies
		const renderField = React.useCallback(
			(field: IFormField<string>, index: number) => {
				// Calculate field state - simplified and memoized
				const fieldErrors = formErrors[index] || {};
				const hasError = fieldErrors.label?.message || fieldErrors.options?.message;
				const hasBeenSaved = !!field._type && !isClone;
				const isDirty = isFieldDirty(field.id, index);

				// Create TagManager props if tag generation is enabled AND this is an option field
				let tagManagerProps: TagManagerProps | undefined;
				const isOptionField =
					field.fieldType === FormFieldType.MultipleOptions || field.fieldType === FormFieldType.Option;

				if (
					tagGenerationConfig.enabled &&
					tagGenerationConfig.supportsTagPrefixes &&
					isOptionField &&
					onTagPrefixChange &&
					onTagPrefixDelete
				) {
					tagManagerProps = {
						fieldId: field.id,
						fieldLabel: field.label,
						isOptionField,
						questionIndex: index,
						tagGenerationSettings: memoizedTagGenerationSettings,
						onTagPrefixChange,
						onTagPrefixDelete,
						disabled: false, // Allow tag creation for new surveys, existing surveys, and clones
						onValidationTrigger,
					};
				}

				// Create CustomFieldManager props if custom field management is enabled AND field type is supported
				let customFieldManagerProps: CustomFieldManagerProps | undefined;
				const isSupportedFieldType = customFieldConfig.supportedFieldTypes.includes(field.fieldType);

				if (
					customFieldConfig.enabled &&
					isSupportedFieldType &&
					onCustomFieldChange &&
					onCustomFieldDelete
					// Temporarily removed field.label?.trim() requirement for debugging
				) {
					// Filter out the current field's mapping from used IDs to allow editing existing mappings
					const currentFieldMapping = memoizedCustomFieldMap?.find(mapping => mapping.fieldId === field.id);
					const filteredUsedCustomFieldIds = usedCustomFieldIds.filter(
						usedId => usedId !== currentFieldMapping?.contactFieldId
					);

					customFieldManagerProps = {
						fieldId: field.id,
						fieldLabel: field.label,
						isOptionField,
						customFieldMap: memoizedCustomFieldMap,
						onCustomFieldChange,
						onCustomFieldDelete,
						disabled: false, // Allow custom field creation for new surveys, existing surveys, and clones
						availableCustomFields, // Pass available custom fields from API
						usedCustomFieldIds: filteredUsedCustomFieldIds, // Pass filtered used field IDs for disabled state
						onValidationTrigger,
						questionIndex: index,
					};
				}

				// Create ConditionalQuestionControls props if conditional questions are enabled
				let conditionalQuestionProps: ConditionalQuestionControlsProps | undefined;
				if (conditionalConfig?.enabled && onConditionalToggle && onRatingThresholdChange && getFieldMetadata) {
					const metadata = getFieldMetadata(field.id);
					conditionalQuestionProps = {
						field,
						fieldIndex: index,
						config: conditionalConfig,
						metadata,
						onConditionalToggle,
						onRatingThresholdChange,
					};
				}

				// Create ConditionalVisibility props for surveys that support it
				let conditionalVisibilityProps: ConditionalVisibilityProps | undefined;
				if (
					conditionalVisibilityConfig?.enabled &&
					onConditionalVisibilityToggle &&
					onConditionalVisibilityQuestionSelect &&
					onConditionalVisibilityAnswerSelect &&
					onConditionalVisibilityAnswerAdd &&
					onConditionalVisibilityAnswerRemove
				) {
					conditionalVisibilityProps = {
						field,
						fieldIndex: index,
						allFields: fields,
						state: conditionalVisibilityMap?.get(field.id),
						onToggle: onConditionalVisibilityToggle,
						onQuestionSelect: onConditionalVisibilityQuestionSelect,
						onAnswerSelect: onConditionalVisibilityAnswerSelect,
						onAnswerAdd: onConditionalVisibilityAnswerAdd,
						onAnswerRemove: onConditionalVisibilityAnswerRemove,
					};
				}

				return (
					<QuestionField
						key={`${field.id}-question-${index}`}
						field={field}
						questionIndex={index}
						isEdit={isEdit}
						isDirty={isDirty}
						hasError={!!hasError}
						hasBeenSaved={hasBeenSaved}
						tagManagerProps={tagManagerProps}
						customFieldManagerProps={customFieldManagerProps}
						conditionalQuestionProps={conditionalQuestionProps}
						conditionalVisibilityProps={conditionalVisibilityProps}
						onFieldUpdate={onFieldUpdate}
						onFieldRemove={onFieldRemove}
						onLabelChange={onLabelChange}
						onToggleOptional={onToggleOptional}
						onFieldTypeChange={onFieldTypeChange}
						onValidationTrigger={onValidationTrigger}
						templateMode={templateMode}
						// supportedFieldTypes restricts the field-type dropdown to specific types.
						// Checked independently of `enabled` (which gates add/remove/reorder).
						formFieldOptions={
							customFieldConfig?.supportedFieldTypes?.length
								? customFieldConfig.supportedFieldTypes.map(type => {
										const title = FormFieldOptionTitles[type];
										return {
											title,
											value: type,
										};
									})
								: undefined
						}
					/>
				);
			},
			[
				formErrors,
				isClone,
				isFieldDirty,
				tagGenerationConfig,
				memoizedTagGenerationSettings,
				customFieldConfig,
				memoizedCustomFieldMap,
				availableCustomFields,
				usedCustomFieldIds,
				conditionalConfig,
				conditionalVisibilityConfig,
				onTagPrefixChange,
				onTagPrefixDelete,
				onCustomFieldChange,
				onCustomFieldDelete,
				onConditionalToggle,
				onRatingThresholdChange,
				getFieldMetadata,
				isEdit,
				onFieldUpdate,
				onFieldRemove,
				onLabelChange,
				onToggleOptional,
				onFieldTypeChange,
				onValidationTrigger,
				templateMode,
				conditionalVisibilityMap,
				onConditionalVisibilityToggle,
				onConditionalVisibilityQuestionSelect,
				onConditionalVisibilityAnswerSelect,
				onConditionalVisibilityAnswerAdd,
				onConditionalVisibilityAnswerRemove,
				fields,
				config.surveyType,
			]
		);

		return (
			<>
				{/* Render all fields */}
				{fields.map(renderField)}

				{/* Add Question Button */}
				{!isPreviewOnly && (
					<div className={css(bs.flex, bs.justifyEnd)}>
						<Button
							type='button'
							styles={[styleSheet.addQuestionButton]}
							onClick={handleAddField}
							disabled={!canAddQuestions}
						>
							+ Add Question
							{!!config.fieldManagement.questionLimit && (
								<span>
									{' '}
									({fields.length}/{config.fieldManagement.questionLimit})
								</span>
							)}
						</Button>
					</div>
				)}
			</>
		);
	}
);

FieldList.displayName = 'FieldList';
